/**
 * NR496 ve PMS raporlarının metinleri (plan M7 Task 28–29; ADR 0029 K23, K27, K33, K37): grup
 * ve rapor adları, sütun başlıkları, kodların metni, özet ve dışa aktarma (PDF, Excel, CSV)
 * başlıkları. Rapor kataloğu `@felix/domain` `PMS_REPORTS`'tadır; `locales.test.ts` her rapor,
 * tablo ve sütunun iki dilde metni olduğunu sınar [NR496 4.1.5].
 */

export const pmsReportsEn = {
  groups: {
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    class: 'Class',
    nr496: 'NR496',
  },
  reports: {
    'daily-maintenance': {
      title: 'Daily Maintenance Report',
      description: 'Jobs completed on the day and jobs due or overdue',
    },
    'pms-status': {
      title: 'PMS Status',
      description: 'Every active job with its last done, due and status',
    },
    overdue: {
      title: 'Overdue Report',
      description: 'Overdue jobs; a returned record does not count as done',
    },
    'critical-equipment': {
      title: 'Critical Equipment Report',
      description: 'Critical equipment, its jobs and open defects',
    },
    'pms-compliance': {
      title: 'PMS Compliance',
      description: 'Jobs due in the period and overdue at its end (BIMCO PI065)',
    },
    breakdowns: {
      title: 'Breakdown Report',
      description: 'Breakdowns reported in the period and their closure',
    },
    defects: {
      title: 'Defect Report',
      description: 'Defects reported in the period and their closure',
    },
    'spare-parts': {
      title: 'Spare Parts Consumption',
      description: 'Parts used by work records and reversals of voided records',
    },
    'class-due-list': {
      title: 'Class Due List',
      description: 'Class items, last chief engineer survey and confirmation',
    },
    'survey-status': {
      title: 'Survey Status',
      description: 'Survey schedule with windows and state',
    },
    'vessel-certificates': {
      title: 'Vessel Certificates',
      description: 'Certificates with expiry, next survey window and status',
    },
    'conditions-of-class': {
      title: 'Conditions of Class',
      description: 'Conditions of class, memoranda and recommendations',
    },
    'annual-pms': {
      title: 'Annual PMS Report',
      description: 'Equipment list, job descriptions and changes in the year',
    },
    'running-hours': {
      title: 'Running Hours',
      description: 'Readings and meter changes with meter value and accumulated total',
    },
    postponements: {
      title: 'Postponements',
      description: 'Cycle, first original due date, every postponement and decision',
    },
    'maintenance-history': {
      title: 'Maintenance History',
      description: 'Work records of the period, voided records with their reason',
    },
    'maintenance-plan': {
      title: 'Maintenance Plan',
      description: 'Job descriptions with intervals, class and critical flags',
    },
    'equipment-list': {
      title: 'Equipment List',
      description: 'Equipment with class and critical flags',
    },
    'equipment-changes': {
      title: 'Equipment Change History',
      description: 'Field changes of equipment: old and new value, who, where and when',
    },
    'job-changes': {
      title: 'Job Definition Change History',
      description: 'Field changes of job definitions: old and new value, who, where and when',
    },
    'interval-changes': {
      title: 'Interval Changes',
      description: 'Interval extensions and reductions; class item interval exceeded is flagged',
    },
    'cm-data': {
      title: 'CM Data & Limit Changes',
      description: 'Condition monitoring imports, measurements and limit changes',
    },
    'late-entries': {
      title: 'Late Entries',
      description: 'Work records entered more than the threshold after the work was done',
    },
  },
  tables: {
    completed: 'Completed',
    pending: 'Due and overdue',
    jobs: 'Jobs',
    equipment: 'Equipment',
    defects: 'Records',
    movements: 'Movements',
    items: 'Class items',
    surveys: 'Surveys',
    findings: 'Findings',
    changes: 'Changes',
    readings: 'Readings',
    postponements: 'Postponements',
    records: 'Work records',
    imports: 'Imports',
    limitChanges: 'Limit and baseline changes',
    measurements: 'Measurements',
    certificates: 'Certificates',
  },
  columns: {
    active: 'Active',
    at: 'Time (UTC)',
    bvItemCode: 'Class Item',
    category: 'Category',
    ceCredited: 'Credited',
    ceSurvey: 'CE Survey',
    classItem: 'Class Item',
    classItemMonths: 'Class Item Interval (months)',
    classNotification: 'Class Notification',
    classReference: 'Class Reference',
    classRelevant: 'Class',
    closedOn: 'Closed',
    closure: 'Closure',
    code: 'Code',
    conditionAfter: 'Condition After',
    conditionPoints: 'Condition Points',
    confirmations: 'Confirmations',
    contentHash: 'Content Hash',
    critical: 'Critical',
    criticalReason: 'Critical Reason',
    daysLeft: 'Days Left',
    expiryDate: 'Expiry',
    issueDate: 'Issued',
    issuedBy: 'Issued By',
    nextSurveyDue: 'Next Survey',
    nextSurveyKind: 'Survey Type',
    officeEntryPending: 'Office Entry Pending',
    pages: 'Pages',
    superseded: 'Superseded',
    typeCode: 'Type',
    criticalSource: 'Critical Via',
    cumulativeHours: 'Accumulated Hours',
    currentHours: 'Current Hours',
    cycleBase: 'Cycle',
    date: 'Date',
    decisions: 'Decisions',
    defectNo: 'No.',
    defectsOpened: 'Defects Opened',
    delayDays: 'Delay (days)',
    description: 'Description',
    device: 'Computer',
    direction: 'Direction',
    doneOn: 'Done',
    dueHours: 'Due (h)',
    dueOn: 'Due',
    enteredAt: 'Entered (UTC)',
    enteredBy: 'Entered By',
    equipment: 'Equipment',
    equipmentCode: 'Equipment Code',
    equipmentName: 'Equipment',
    exceedsClassItem: 'Exceeds Class Item',
    field: 'Field',
    fileName: 'File',
    firstOriginalDueHours: 'First Original Due (h)',
    firstOriginalDueOn: 'First Original Due',
    hasCounter: 'Counter',
    importedAt: 'Imported (UTC)',
    imposedOn: 'Imposed',
    instruction: 'Instruction',
    intervalBasis: 'Interval Basis',
    intervalEvery: 'Interval',
    intervalHours: 'Interval (h)',
    intervalMonths: 'Interval (months)',
    intervalUnit: 'Unit',
    itemCode: 'Item Code',
    jobCode: 'Job Code',
    jobs: 'Jobs',
    jobTitle: 'Job',
    jobType: 'Job Type',
    kind: 'Type',
    lastConfirmedOn: 'Confirmed',
    lastDoneHours: 'Last Done (h)',
    lastDoneOn: 'Last Done',
    lastSurveyOn: 'Last CE Survey',
    location: 'Location',
    maker: 'Maker',
    material: 'Material',
    matrixVersion: 'Matrix',
    measuredAt: 'Measured (UTC)',
    measurementsAdded: 'Measurements',
    meterHours: 'Meter Value',
    meterStartHours: 'New Meter Start',
    mode: 'Mode',
    model: 'Model',
    name: 'Name',
    newDueHours: 'New Due (h)',
    newDueOn: 'New Due',
    newEvery: 'New Interval',
    newHours: 'New Interval (h)',
    newUnit: 'New Unit',
    newValue: 'New Value',
    nextDueOn: 'Next Due',
    nodeType: 'Node Type',
    number: 'No.',
    oldValue: 'Old Value',
    op: 'Operation',
    openDefects: 'Open Defects',
    operationalImpact: 'Operational Impact',
    outOfPms: 'Out of PMS',
    overdue: 'Overdue',
    overdueDays: 'Overdue (days)',
    overdueHours: 'Overdue (h)',
    overdueReason: 'Reason for Overdue',
    overrideReason: 'Override Reason',
    parentCode: 'Parent',
    pendingApproval: 'Pending Approval',
    performedBy: 'Performed By',
    point: 'Point',
    pointsCreated: 'Points Created',
    pointsUpdated: 'Points Updated',
    postponementPending: 'Postponement Pending',
    postponementReason: 'Reason for Postponement',
    postponedOn: 'Postponed On',
    procedure: 'Procedure',
    derivedFrom: 'Derived From',
    safetyItems: 'Safety Items',
    previousDueHours: 'Previous Due (h)',
    previousDueOn: 'Previous Due',
    previousEvery: 'Previous Interval',
    previousHours: 'Previous Interval (h)',
    previousMeterFinal: 'Old Meter Final',
    previousUnit: 'Previous Unit',
    priority: 'Priority',
    provider: 'Provider',
    quantity: 'Quantity',
    readOn: 'Read',
    reapprovalPending: 'Reapproval Pending',
    reason: 'Reason',
    rejection: 'Not Credited Because',
    remainingDays: 'Remaining (days)',
    remainingHours: 'Remaining (h)',
    remarks: 'Remarks',
    repairWoNo: 'Repair WO',
    reportedOn: 'Reported',
    requestedAt: 'Requested (UTC)',
    requestedBy: 'Requested By',
    responsible: 'Responsible',
    result: 'Result',
    rowsTotal: 'Rows',
    runningHours: 'Running Hours',
    serialNo: 'Serial No.',
    severity: 'Severity',
    sfiCode: 'SFI',
    sha256: 'SHA-256',
    shipTime: 'Ship Time',
    source: 'Source',
    state: 'State',
    status: 'Status',
    surveyor: 'Surveyor',
    surveyPrinciple: 'Survey Principle',
    table: 'Record',
    text: 'Text',
    unit: 'Unit',
    user: 'User',
    value: 'Value',
    voidReason: 'Void Reason',
    windowFrom: 'Window From',
    windowTo: 'Window To',
    woNo: 'WO No.',
    workPerformed: 'Work Performed',
    workType: 'Work Type',
  },
  /** Kod sütunlarının değerleri; listede olmayan kod olduğu gibi yazılır. */
  codes: {
    on_time: 'On time',
    due_soon: 'Due soon',
    overdue: 'Overdue',
    postponed: 'Postponed',
    pending: 'Pending approval',
    approved: 'Approved',
    returned: 'Returned',
    revoked: 'Revoked by office',
    voided: 'VOID',
    draft: 'Not submitted',
    completed: 'Completed',
    open: 'Open',
    closed: 'Closed',
    immediate: 'Effective immediately',
    initial: 'First cycle',
    pass: 'Pass',
    fail: 'Fail',
    normal: 'Normal',
    abnormal: 'Abnormal',
    operational: 'Operational',
    operational_with_restrictions: 'Operational with restrictions',
    not_operational: 'Not operational',
    minor: 'Minor',
    major: 'Major',
    critical: 'Critical',
    investigating: 'Investigating',
    repair: 'Repair',
    testing: 'Testing',
    verification: 'Verification',
    planned: 'Planned',
    unplanned: 'Unplanned',
    corrective: 'Corrective',
    breakdown: 'Breakdown',
    defect: 'Defect',
    reading: 'Reading',
    running_hours: 'Running hours entered',
    derived: 'Derived from equipment',
    void: 'Voided',
    meter_replaced: 'Meter replaced',
    meter_reset: 'Meter reset',
    consumed: 'Consumed',
    reversal: 'Reversal (void)',
    manual: 'Manual',
    noon_report: 'Noon Report',
    cm_import: 'CM import',
    measurement: 'Measurement',
    work_record: 'Work record',
    group: 'Group',
    system: 'System',
    equipment: 'Equipment',
    component: 'Component',
    machinery: 'Machinery',
    electrical: 'Electrical',
    pressure_vessel: 'Pressure vessel',
    boiler: 'Boiler',
    tailshaft: 'Tailshaft',
    other: 'Other',
    pms_ce: 'PMS (chief engineer)',
    surveyor: 'Surveyor',
    condition_monitoring: 'Condition monitoring',
    none: 'None',
    never_surveyed: 'Never surveyed',
    not_credited: 'Not credited',
    awaiting_confirmation: 'Awaiting confirmation',
    confirmed: 'Confirmed',
    upcoming: 'Upcoming',
    UNKNOWN_BV_ITEM: 'Unknown class item',
    CE_SURVEY_NOT_ALLOWED: 'Item not open to chief engineer survey',
    CE_NOT_AUTHORIZED: 'No valid authorization',
    annual: 'Annual',
    intermediate: 'Intermediate',
    special: 'Special (renewal)',
    bottom: 'Bottom',
    safety_equipment: 'Safety equipment',
    radio: 'Radio',
    coc: 'Condition of class',
    memorandum: 'Memorandum',
    recommendation: 'Recommendation',
    preventive: 'Preventive',
    inspection: 'Inspection',
    test: 'Test',
    condition_based: 'Condition based',
    class_survey: 'Class survey',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    maker: 'Maker',
    class: 'Class',
    experience: 'Experience',
    company: 'Company',
    day: 'day',
    week: 'week',
    month: 'month',
    year: 'year',
    extend: 'Extension',
    shorten: 'Reduction',
    thresholds: 'Limits',
    thresholds_and_values: 'Limits and values',
    insert: 'Created',
    update: 'Changed',
    delete: 'Removed',
    job_definitions: 'Job definition',
    job_interval_changes: 'Interval change',
    measurement_points: 'Measurement point',
    prepared_in_office: 'Prepared in office before activation',
    created_in_office: 'Created in office',
    changed_in_office: 'Changed in office',
    valid: 'Valid',
    expiring: 'Expiring',
    expired: 'Expired',
    statutory: 'Statutory',
    safety: 'Safety',
    environmental: 'Environmental',
    flag_port: 'Flag / Port',
    renewal: 'Renewal',
    office: 'Office',
    on_board: 'Issued on board',
  },
  summary: {
    active: 'Active',
    approved: 'Approved',
    ceSurveys: 'Credited CE surveys',
    changes: 'Changes',
    classItem: 'Class items',
    classRelevant: 'Class related',
    closed: 'Closed',
    completed: 'Completed',
    consumed: 'Consumed',
    critical: 'Critical',
    criticalOverdue: 'Critical overdue',
    due: 'Due in period',
    dueNext30Days: 'Due next 30 days',
    dueNext7Days: 'Due next 7 days',
    equipment: 'Equipment',
    exceedsClassItem: 'Exceeding class item',
    extensions: 'Extensions',
    findings: 'Findings',
    imports: 'Imports',
    items: 'Items',
    jobs: 'Jobs',
    lateEntries: 'Late entries',
    limitChanges: 'Limit changes',
    measurements: 'Measurements',
    meterChanges: 'Meter changes',
    movements: 'Movements',
    open: 'Open',
    outOfLimits: 'Out of limits',
    overdue: 'Overdue',
    pending: 'Pending',
    percent: 'PMS compliance (%)',
    postponed: 'Postponed',
    postponements: 'Postponements',
    readings: 'Readings',
    reapprovalPending: 'Reapproval pending',
    records: 'Records',
    returned: 'Returned',
    reversed: 'Reversed',
    surveys: 'Surveys',
    thresholdDays: 'Threshold (days)',
    total: 'Total',
    valid: 'Valid',
    expiring: 'Expiring',
    expired: 'Expired',
    voided: 'Voided',
  },
  basis: {
    records: 'Immutable records, audit log and approved or reported records',
    master_data: 'Master data at the time of printing',
    mixed: 'Records and master data at the time of printing',
  },
  notes: {
    ship_changes_only:
      'This vessel version lists only the changes made on board. The full field history is in the office version.',
    as_of: 'Plan calculated for the end of the period; approval states are as of printing.',
  },
  marks: {
    void: 'VOID',
    reapproval_pending: 'Reapproval pending',
    deleted: 'Deleted',
  },
  ui: {
    title: 'NR496 and PMS reports',
    description:
      'Reports are produced from immutable records, the audit log and approved or reported records. They can be viewed and exported; they cannot be changed.',
    group: '{{group}} reports',
    preview: 'Preview',
    previewOf: 'Preview {{name}}',
    pdfOf: '{{name}}: download PDF',
    excelOf: '{{name}}: export to Excel',
    csvOf: '{{name}}: export to CSV',
    back: 'All reports',
    from: 'From',
    to: 'To',
    day: 'Day',
    year: 'Year',
    asOf: 'As of {{date}}',
    period: '{{from}} – {{to}}',
    source: 'Source',
    rule: 'Basis',
    hash: 'Report hash',
    generated: 'Generated {{at}} UTC',
    loading: 'Loading the report…',
    error: 'The report could not be loaded.',
    retry: 'Retry',
    empty: 'No rows for the selected period and filters.',
    showing: 'Showing {{from}} to {{to}} of {{total}} rows',
    pages: 'Pages of {{name}}',
    tableOf: '{{name}} table',
    mark: 'Mark',
    pdfFailed: 'The PDF could not be created.',
    pmsTitle: 'Maintenance (PMS)',
    pmsText: 'NR496 and PMS reports open under Maintenance (PMS) ▸ History ▸ Reports.',
  },
  filters: {
    title: 'Filters',
    class: 'Class',
    critical: 'Critical',
    planning: 'Planned / unplanned',
    overdue: 'Reported overdue',
    postponed: 'Reported postponed',
    any: 'All',
    yes: 'Yes',
    no: 'No',
    planned: 'Planned',
    unplanned: 'Unplanned',
  },
  yes: 'Yes',
  no: 'No',
  export: {
    about: 'About',
    software: 'Software',
    report: 'Report',
    group: 'Group',
    vessel: 'Vessel',
    period: 'Period',
    filters: 'Filters',
    asOf: 'As of',
    equipment: 'Equipment',
    source: 'Source',
    tables: 'Tables read',
    rule: 'Basis',
    printedAt: 'Printed (UTC)',
    printedAtShip: 'Printed (ship time)',
    printedBy: 'Printed by',
    hash: 'Report hash',
    mark: 'Mark',
    notes: 'Notes',
    summary: 'Summary',
    empty: 'No records.',
    page: 'Page {{page}} of {{pages}}',
    footer: '{{product}} {{version}} · Printed {{utc}} UTC / {{ship}} ship time · {{person}}',
    excel: 'Excel',
    csv: 'CSV',
    pdf: 'Download PDF',
    export: 'Export',
    exportHint: 'Export the selected equipment and its sub-items',
    exporting: 'Preparing the file…',
    exportFailed: 'The file could not be prepared.',
  },
};

export type PmsReportsResources = typeof pmsReportsEn;

export const pmsReportsTr: PmsReportsResources = {
  groups: {
    daily: 'Günlük',
    weekly: 'Haftalık',
    monthly: 'Aylık',
    class: 'Klas',
    nr496: 'NR496',
  },
  reports: {
    'daily-maintenance': {
      title: 'Günlük Bakım Raporu',
      description: 'O gün tamamlanan işler ve vadesi gelen ya da geçen işler',
    },
    'pms-status': {
      title: 'PMS Durumu',
      description: 'Bütün etkin işler: son yapılış, vade ve durum',
    },
    overdue: {
      title: 'Gecikme Raporu',
      description: 'Gecikmiş işler; iade edilen kayıt yapılmış sayılmaz',
    },
    'critical-equipment': {
      title: 'Kritik Ekipman Raporu',
      description: 'Kritik ekipman, işleri ve açık arızaları',
    },
    'pms-compliance': {
      title: 'PMS Uyumu',
      description: 'Dönemde vadesi gelen ve dönem sonunda gecikmiş işler (BIMCO PI065)',
    },
    breakdowns: {
      title: 'Breakdown Raporu',
      description: 'Dönemde bildirilen breakdown kayıtları ve kapanışları',
    },
    defects: {
      title: 'Arıza Raporu',
      description: 'Dönemde bildirilen arızalar ve kapanışları',
    },
    'spare-parts': {
      title: 'Yedek Parça Tüketimi',
      description:
        'İş kayıtlarında kullanılan parçalar ve iptal edilen kayıtların ters hareketleri',
    },
    'class-due-list': {
      title: 'Klas Vade Listesi',
      description: 'Klas kalemleri, son başmühendis sörveyi ve teyidi',
    },
    'survey-status': {
      title: 'Sörvey Durumu',
      description: 'Sörvey takvimi, pencereler ve durum',
    },
    'vessel-certificates': {
      title: 'Gemi Sertifikaları',
      description: 'Bitiş, sıradaki sörvey penceresi ve durumla sertifikalar',
    },
    'conditions-of-class': {
      title: 'Klas Şartları',
      description: 'Klas şartları, memorandumlar ve tavsiyeler',
    },
    'annual-pms': {
      title: 'Yıllık PMS Raporu',
      description: 'Ekipman listesi, iş föyleri ve yıl içindeki değişiklikler',
    },
    'running-hours': {
      title: 'Çalışma Saatleri',
      description: 'Okumalar ve sayaç değişimleri: sayaç değeri ve birikmiş toplam',
    },
    postponements: {
      title: 'Ertelemeler',
      description: 'Döngü, ilk orijinal vade, bütün ertelemeler ve kararlar',
    },
    'maintenance-history': {
      title: 'Bakım Geçmişi',
      description: 'Dönemin iş kayıtları; iptal edilenler gerekçesiyle',
    },
    'maintenance-plan': {
      title: 'Bakım Planı',
      description: 'İş tanımları: aralık, klas ve kritik işaretleri',
    },
    'equipment-list': {
      title: 'Ekipman Listesi',
      description: 'Klas ve kritik işaretleriyle ekipman',
    },
    'equipment-changes': {
      title: 'Ekipman Değişiklik Geçmişi',
      description: 'Ekipman alanlarının değişiklikleri: eski ve yeni değer, kim, nerede, ne zaman',
    },
    'job-changes': {
      title: 'İş Tanımı Değişiklik Geçmişi',
      description:
        'İş tanımı alanlarının değişiklikleri: eski ve yeni değer, kim, nerede, ne zaman',
    },
    'interval-changes': {
      title: 'Aralık Değişiklikleri',
      description: 'Aralık uzatma ve kısaltmaları; klas kalemi aralığını aşan işaretli',
    },
    'cm-data': {
      title: 'CM Verisi ve Limit Değişiklikleri',
      description: 'Durum izleme içe aktarmaları, ölçümler ve limit değişiklikleri',
    },
    'late-entries': {
      title: 'Geç Girişler',
      description: 'İşin yapılışından eşikten fazla gün sonra girilen iş kayıtları',
    },
  },
  tables: {
    completed: 'Tamamlanan',
    pending: 'Vadesi gelen ve geçen',
    jobs: 'İşler',
    equipment: 'Ekipman',
    defects: 'Kayıtlar',
    movements: 'Hareketler',
    items: 'Klas kalemleri',
    surveys: 'Sörveyler',
    findings: 'Bulgular',
    changes: 'Değişiklikler',
    readings: 'Okumalar',
    postponements: 'Ertelemeler',
    records: 'İş kayıtları',
    imports: 'İçe aktarmalar',
    limitChanges: 'Limit ve baseline değişiklikleri',
    measurements: 'Ölçümler',
    certificates: 'Sertifikalar',
  },
  columns: {
    active: 'Etkin',
    at: 'Zaman (UTC)',
    bvItemCode: 'Klas Kalemi',
    category: 'Kategori',
    ceCredited: 'Sayıldı',
    ceSurvey: 'BM Sörveyi',
    classItem: 'Klas Kalemi',
    classItemMonths: 'Klas Kalemi Aralığı (ay)',
    classNotification: 'Klas Bildirimi',
    classReference: 'Klas Referansı',
    classRelevant: 'Klas',
    closedOn: 'Kapanış',
    closure: 'Kapanış Onayı',
    code: 'Kod',
    conditionAfter: 'Sonraki Durum',
    conditionPoints: 'Ölçüm Noktaları',
    confirmations: 'Onaylar',
    contentHash: 'İçerik Hash',
    critical: 'Kritik',
    criticalReason: 'Kritiklik Gerekçesi',
    daysLeft: 'Kalan Gün',
    expiryDate: 'Bitiş',
    issueDate: 'Düzenlendi',
    issuedBy: 'Düzenleyen',
    nextSurveyDue: 'Sıradaki Sörvey',
    nextSurveyKind: 'Sörvey Türü',
    officeEntryPending: 'Ofis Kaydı Bekliyor',
    pages: 'Sayfa',
    superseded: 'Yenilendi',
    typeCode: 'Tür',
    criticalSource: 'Kritiklik Kaynağı',
    cumulativeHours: 'Birikmiş Saat',
    currentHours: 'Güncel Saat',
    cycleBase: 'Döngü',
    date: 'Tarih',
    decisions: 'Kararlar',
    defectNo: 'No',
    defectsOpened: 'Açılan Arıza',
    delayDays: 'Gecikme (gün)',
    description: 'Açıklama',
    device: 'Bilgisayar',
    direction: 'Yön',
    doneOn: 'Yapıldı',
    dueHours: 'Vade (s)',
    dueOn: 'Vade',
    enteredAt: 'Girildi (UTC)',
    enteredBy: 'Giren',
    equipment: 'Ekipman',
    equipmentCode: 'Ekipman Kodu',
    equipmentName: 'Ekipman',
    exceedsClassItem: 'Klas Kalemini Aşar',
    field: 'Alan',
    fileName: 'Dosya',
    firstOriginalDueHours: 'İlk Orijinal Vade (s)',
    firstOriginalDueOn: 'İlk Orijinal Vade',
    hasCounter: 'Sayaç',
    importedAt: 'İçe Aktarıldı (UTC)',
    imposedOn: 'Konuldu',
    instruction: 'Talimat',
    intervalBasis: 'Aralık Dayanağı',
    intervalEvery: 'Aralık',
    intervalHours: 'Aralık (s)',
    intervalMonths: 'Aralık (ay)',
    intervalUnit: 'Birim',
    itemCode: 'Kalem Kodu',
    jobCode: 'İş Kodu',
    jobs: 'İşler',
    jobTitle: 'İş',
    jobType: 'İş Türü',
    kind: 'Tür',
    lastConfirmedOn: 'Teyit',
    lastDoneHours: 'Son Yapılış (s)',
    lastDoneOn: 'Son Yapılış',
    lastSurveyOn: 'Son BM Sörveyi',
    location: 'Konum',
    maker: 'Üretici',
    material: 'Malzeme',
    matrixVersion: 'Matris',
    measuredAt: 'Ölçüldü (UTC)',
    measurementsAdded: 'Ölçüm',
    meterHours: 'Sayaç Değeri',
    meterStartHours: 'Yeni Sayaç Başlangıcı',
    mode: 'Kip',
    model: 'Model',
    name: 'Ad',
    newDueHours: 'Yeni Vade (s)',
    newDueOn: 'Yeni Vade',
    newEvery: 'Yeni Aralık',
    newHours: 'Yeni Aralık (s)',
    newUnit: 'Yeni Birim',
    newValue: 'Yeni Değer',
    nextDueOn: 'Sonraki Vade',
    nodeType: 'Düğüm Türü',
    number: 'No',
    oldValue: 'Eski Değer',
    op: 'İşlem',
    openDefects: 'Açık Arıza',
    operationalImpact: 'Operasyonel Etki',
    outOfPms: 'PMS Dışı',
    overdue: 'Gecikmiş',
    overdueDays: 'Gecikme (gün)',
    overdueHours: 'Gecikme (s)',
    overdueReason: 'Gecikme Gerekçesi',
    overrideReason: 'Elle Değiştirme Gerekçesi',
    parentCode: 'Üst',
    pendingApproval: 'Onay Bekliyor',
    performedBy: 'Yapan',
    point: 'Nokta',
    pointsCreated: 'Açılan Nokta',
    pointsUpdated: 'Güncellenen Nokta',
    postponementPending: 'Erteleme Bekliyor',
    postponementReason: 'Erteleme Gerekçesi',
    postponedOn: 'Erteleme Tarihi',
    procedure: 'Prosedür',
    derivedFrom: 'Kaynak Ekipman',
    safetyItems: 'Emniyet Maddeleri',
    previousDueHours: 'Önceki Vade (s)',
    previousDueOn: 'Önceki Vade',
    previousEvery: 'Önceki Aralık',
    previousHours: 'Önceki Aralık (s)',
    previousMeterFinal: 'Eski Sayaç Son Değeri',
    previousUnit: 'Önceki Birim',
    priority: 'Öncelik',
    provider: 'Sağlayıcı',
    quantity: 'Miktar',
    readOn: 'Okuma Günü',
    reapprovalPending: 'Yeniden Onay Bekliyor',
    reason: 'Gerekçe',
    rejection: 'Sayılmama Nedeni',
    remainingDays: 'Kalan (gün)',
    remainingHours: 'Kalan (s)',
    remarks: 'Notlar',
    repairWoNo: 'Onarım İE',
    reportedOn: 'Bildirildi',
    requestedAt: 'İstendi (UTC)',
    requestedBy: 'İsteyen',
    responsible: 'Sorumlu',
    result: 'Sonuç',
    rowsTotal: 'Satır',
    runningHours: 'Çalışma Saati',
    serialNo: 'Seri No',
    severity: 'Önem',
    sfiCode: 'SFI',
    sha256: 'SHA-256',
    shipTime: 'Gemi Saati',
    source: 'Kaynak',
    state: 'Durum',
    status: 'Durum',
    surveyor: 'Sörveyör',
    surveyPrinciple: 'Sörvey Prensibi',
    table: 'Kayıt',
    text: 'Metin',
    unit: 'Birim',
    user: 'Kullanıcı',
    value: 'Değer',
    voidReason: 'İptal Gerekçesi',
    windowFrom: 'Pencere Başı',
    windowTo: 'Pencere Sonu',
    woNo: 'İE No',
    workPerformed: 'Yapılan İş',
    workType: 'İş Tipi',
  },
  codes: {
    on_time: 'Zamanında',
    due_soon: 'Vadesi yakın',
    overdue: 'Gecikmiş',
    postponed: 'Ertelendi',
    pending: 'Onay bekliyor',
    approved: 'Onaylandı',
    returned: 'İade edildi',
    revoked: 'Ofis geri çevirdi',
    voided: 'İPTAL',
    draft: 'Gönderilmedi',
    completed: 'Tamamlandı',
    open: 'Açık',
    closed: 'Kapalı',
    immediate: 'Hemen etkili',
    initial: 'İlk döngü',
    pass: 'Geçti',
    fail: 'Kaldı',
    normal: 'Normal',
    abnormal: 'Anormal',
    operational: 'Çalışır',
    operational_with_restrictions: 'Kısıtlı çalışır',
    not_operational: 'Çalışmaz',
    minor: 'Küçük',
    major: 'Büyük',
    critical: 'Kritik',
    investigating: 'İnceleniyor',
    repair: 'Onarım',
    testing: 'Test',
    verification: 'Doğrulama',
    planned: 'Planlı',
    unplanned: 'Plansız',
    corrective: 'Düzeltici',
    breakdown: 'Breakdown',
    defect: 'Arıza',
    reading: 'Okuma',
    running_hours: 'Çalışma saati girildi',
    derived: 'Ekipmandan türetildi',
    void: 'İptal edildi',
    meter_replaced: 'Sayaç değişti',
    meter_reset: 'Sayaç sıfırlandı',
    consumed: 'Tüketildi',
    reversal: 'Ters hareket (iptal)',
    manual: 'Elle',
    noon_report: 'Noon Report',
    cm_import: 'CM içe aktarma',
    measurement: 'Ölçüm',
    work_record: 'İş kaydı',
    group: 'Grup',
    system: 'Sistem',
    equipment: 'Ekipman',
    component: 'Bileşen',
    machinery: 'Makine',
    electrical: 'Elektrik',
    pressure_vessel: 'Basınçlı kap',
    boiler: 'Kazan',
    tailshaft: 'Pervane şaftı',
    other: 'Diğer',
    pms_ce: 'PMS (başmühendis)',
    surveyor: 'Sörveyör',
    condition_monitoring: 'Durum izleme',
    none: 'Yok',
    never_surveyed: 'Hiç sörvey yapılmadı',
    not_credited: 'Sayılmadı',
    awaiting_confirmation: 'Teyit bekliyor',
    confirmed: 'Teyit edildi',
    upcoming: 'Yaklaşıyor',
    UNKNOWN_BV_ITEM: 'Bilinmeyen klas kalemi',
    CE_SURVEY_NOT_ALLOWED: 'Kalem başmühendis sörveyine açık değil',
    CE_NOT_AUTHORIZED: 'Geçerli yetki beyanı yok',
    annual: 'Yıllık',
    intermediate: 'Ara',
    special: 'Özel (yenileme)',
    bottom: 'Havuz',
    safety_equipment: 'Emniyet teçhizatı',
    radio: 'Telsiz',
    coc: 'Klas şartı',
    memorandum: 'Memorandum',
    recommendation: 'Tavsiye',
    preventive: 'Önleyici',
    inspection: 'Muayene',
    test: 'Test',
    condition_based: 'Duruma dayalı',
    class_survey: 'Klas sörveyi',
    low: 'Düşük',
    medium: 'Orta',
    high: 'Yüksek',
    maker: 'Üretici',
    class: 'Klas',
    experience: 'Tecrübe',
    company: 'Şirket',
    day: 'gün',
    week: 'hafta',
    month: 'ay',
    year: 'yıl',
    extend: 'Uzatma',
    shorten: 'Kısaltma',
    thresholds: 'Limitler',
    thresholds_and_values: 'Limitler ve değerler',
    insert: 'Oluşturuldu',
    update: 'Değiştirildi',
    delete: 'Kayıttan kaldırıldı',
    job_definitions: 'İş tanımı',
    job_interval_changes: 'Aralık değişikliği',
    measurement_points: 'Ölçüm noktası',
    prepared_in_office: 'Etkinleştirmeden önce ofiste hazırlandı',
    created_in_office: 'Ofiste oluşturuldu',
    changed_in_office: 'Ofiste değiştirildi',
    valid: 'Geçerli',
    expiring: 'Bitiyor',
    expired: 'Süresi doldu',
    statutory: 'Kanuni',
    safety: 'Emniyet',
    environmental: 'Çevre',
    flag_port: 'Bayrak / Liman',
    renewal: 'Yenileme',
    office: 'Ofis',
    on_board: 'Gemide düzenlendi',
  },
  summary: {
    active: 'Etkin',
    approved: 'Onaylı',
    ceSurveys: 'Sayılan BM sörveyleri',
    changes: 'Değişiklik',
    classItem: 'Klas kalemi',
    classRelevant: 'Klasla ilgili',
    closed: 'Kapalı',
    completed: 'Tamamlanan',
    consumed: 'Tüketilen',
    critical: 'Kritik',
    criticalOverdue: 'Kritik gecikme',
    due: 'Dönemde vadesi gelen',
    dueNext30Days: '30 gün içinde vadeli',
    dueNext7Days: '7 gün içinde vadeli',
    equipment: 'Ekipman',
    exceedsClassItem: 'Klas kalemini aşan',
    extensions: 'Uzatma',
    findings: 'Bulgu',
    imports: 'İçe aktarma',
    items: 'Kalem',
    jobs: 'İş',
    lateEntries: 'Geç giriş',
    limitChanges: 'Limit değişikliği',
    measurements: 'Ölçüm',
    meterChanges: 'Sayaç değişimi',
    movements: 'Hareket',
    open: 'Açık',
    outOfLimits: 'Limit dışı',
    overdue: 'Gecikmiş',
    pending: 'Bekleyen',
    percent: 'PMS uyumu (%)',
    postponed: 'Ertelenmiş',
    postponements: 'Erteleme',
    readings: 'Okuma',
    reapprovalPending: 'Yeniden onay bekleyen',
    records: 'Kayıt',
    returned: 'İade edilen',
    reversed: 'Geri alınan',
    surveys: 'Sörvey',
    thresholdDays: 'Eşik (gün)',
    total: 'Toplam',
    valid: 'Geçerli',
    expiring: 'Bitiyor',
    expired: 'Süresi dolan',
    voided: 'İptal edilen',
  },
  basis: {
    records: 'Değiştirilemez kayıtlar, denetim kaydı ve onaylı ya da raporlanmış kayıtlar',
    master_data: 'Basım anındaki ana veri',
    mixed: 'Kayıtlar ve basım anındaki ana veri',
  },
  notes: {
    ship_changes_only:
      'Bu gemi sürümü yalnız gemide yapılan değişiklikleri listeler. Alanların tam geçmişi ofis sürümündedir.',
    as_of: 'Plan dönem sonuna göre hesaplandı; onay durumları basım anındakidir.',
  },
  marks: {
    void: 'İPTAL',
    reapproval_pending: 'Yeniden onay bekliyor',
    deleted: 'Kayıttan kaldırıldı',
  },
  ui: {
    title: 'NR496 ve PMS raporları',
    description:
      'Raporlar değiştirilemez kayıtlardan, denetim kaydından ve onaylı ya da raporlanmış kayıtlardan üretilir. Görüntülenir ve dışa aktarılır; değiştirilemez.',
    group: '{{group}} raporları',
    preview: 'Önizle',
    previewOf: '{{name}} önizle',
    pdfOf: '{{name}}: PDF indir',
    excelOf: '{{name}}: Excel’e aktar',
    csvOf: '{{name}}: CSV’ye aktar',
    back: 'Bütün raporlar',
    from: 'Başlangıç',
    to: 'Bitiş',
    day: 'Gün',
    year: 'Yıl',
    asOf: '{{date}} itibarıyla',
    period: '{{from}} – {{to}}',
    source: 'Kaynak',
    rule: 'Dayanak',
    hash: 'Rapor hash’i',
    generated: '{{at}} UTC’de üretildi',
    loading: 'Rapor yükleniyor…',
    error: 'Rapor yüklenemedi.',
    retry: 'Yeniden dene',
    empty: 'Seçilen dönem ve süzgeçlerde satır yok.',
    showing: '{{total}} satırdan {{from}}–{{to}} gösteriliyor',
    pages: '{{name}} sayfaları',
    tableOf: '{{name}} tablosu',
    mark: 'İşaret',
    pdfFailed: 'PDF oluşturulamadı.',
    pmsTitle: 'Bakım (PMS)',
    pmsText: 'NR496 ve PMS raporları Bakım (PMS) ▸ Geçmiş ▸ Raporlar altında açılır.',
  },
  filters: {
    title: 'Süzgeçler',
    class: 'Klas',
    critical: 'Kritik',
    planning: 'Planlı / plansız',
    overdue: 'Gecikmeli raporlanan',
    postponed: 'Ertelenmiş raporlanan',
    any: 'Tümü',
    yes: 'Evet',
    no: 'Hayır',
    planned: 'Planlı',
    unplanned: 'Plansız',
  },
  yes: 'Evet',
  no: 'Hayır',
  export: {
    about: 'Hakkında',
    software: 'Yazılım',
    report: 'Rapor',
    group: 'Grup',
    vessel: 'Gemi',
    period: 'Dönem',
    filters: 'Süzgeçler',
    asOf: 'Basım günü',
    equipment: 'Ekipman',
    source: 'Kaynak',
    tables: 'Okunan tablolar',
    rule: 'Dayanak',
    printedAt: 'Basım (UTC)',
    printedAtShip: 'Basım (gemi saati)',
    printedBy: 'Basan',
    hash: 'Rapor hash',
    mark: 'İşaret',
    notes: 'Notlar',
    summary: 'Özet',
    empty: 'Kayıt yok.',
    page: 'Sayfa {{page}} / {{pages}}',
    footer: '{{product}} {{version}} · Basım {{utc}} UTC / {{ship}} gemi saati · {{person}}',
    excel: 'Excel',
    csv: 'CSV',
    pdf: 'PDF indir',
    export: 'Dışa aktar',
    exportHint: 'Seçili ekipmanı ve alt kalemlerini dışa aktarır',
    exporting: 'Dosya hazırlanıyor…',
    exportFailed: 'Dosya hazırlanamadı.',
  },
};
